import {
  FORM_FIELDS_ALL_MINIMIZED_KEY,
  FormFieldsVisibilityState,
  getFormFieldsPreferenceKey,
  getHiddenTabIds,
  isAllFieldsMinimized,
  isFieldExpanded,
  isTabVisible,
  setTabVisibility,
  splitFieldLocale,
} from "@/lib/form-field-visibility";
import {
  persistBoPreference,
  readBoPreference,
} from "@/lib/user-preferences/store";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useCurrentTab } from "./CurrentTabContext";
import { AuthContext } from "./auth/AuthContext";

export type MaskableFieldOptions = {
  nonMaskable?: boolean;
  /** When false, masking UI is disabled for this field. */
  maskable?: boolean;
};

/** A maskable field that registered itself while mounted (for the field drawer). */
export type RegisteredVisibilityField = {
  name: string;
  /** i18n key used to label the field in selection UIs (defaults to `name`). */
  labelKey: string;
  /** Resolved display label provided by the field, if any (wins over labelKey). */
  label?: string;
  /** `value` of the enclosing tab, if any. */
  tab?: string;
  nonMaskable?: boolean;
};

/** Options accepted when a field registers itself into the visibility grid. */
export type RegisterFieldOptions = MaskableFieldOptions & {
  labelKey?: string;
  /** Resolved display label (already translated) shown in the drawer/menu. */
  label?: string;
  /** When true, the field does not register (e.g. covered by a parent group). */
  skip?: boolean;
};

type FormFieldVisibilityContextValue = {
  enabled: boolean;
  allFieldsMinimized: boolean;
  visibility: FormFieldsVisibilityState;
  isExpanded: (fieldName: string) => boolean;
  canToggle: (fieldName: string, opts?: MaskableFieldOptions) => boolean;
  toggleVisibility: (fieldName: string, opts?: MaskableFieldOptions) => void;
  minimizeAllFields: () => void;
  expandAllFields: () => void;
  // -- Tab-level visibility --
  isTabVisible: (tabId: string) => boolean;
  toggleTabVisibility: (tabId: string) => void;
  setTabVisible: (tabId: string, visible: boolean) => void;
  hiddenTabIds: string[];
  // -- Runtime field registry (per mounted form) --
  registerField: (field: RegisteredVisibilityField) => () => void;
  registryVersion: number;
  getRegisteredFields: () => RegisteredVisibilityField[];
};

const noop = () => {};

const FormFieldVisibilityContext =
  createContext<FormFieldVisibilityContextValue>({
    enabled: false,
    allFieldsMinimized: false,
    isExpanded: () => true,
    canToggle: () => false,
    toggleVisibility: noop,
    minimizeAllFields: noop,
    expandAllFields: noop,
    visibility: {},
    isTabVisible: () => true,
    toggleTabVisibility: noop,
    setTabVisible: noop,
    hiddenTabIds: [],
    registerField: () => noop,
    registryVersion: 0,
    getRegisteredFields: () => [],
  });

export function useFormFieldVisibility() {
  return useContext(FormFieldVisibilityContext);
}

export function useMaskableField(
  fieldName: string,
  opts?: MaskableFieldOptions,
) {
  const ctx = useFormFieldVisibility();
  // Multilingual fields render once per language (`x.fr`, `x.en`); mask them as
  // a single unit under the base key so visibility is per-field, not per-language.
  const maskKey = splitFieldLocale(fieldName).baseKey;
  const expanded = ctx.isExpanded(maskKey);
  const canToggle = ctx.canToggle(maskKey, opts);

  return {
    enabled: ctx.enabled,
    expanded: !ctx.enabled || opts?.nonMaskable || expanded,
    minimized: ctx.enabled && !opts?.nonMaskable && !expanded,
    canToggle,
    toggleVisibility: () => ctx.toggleVisibility(maskKey, opts),
  };
}

/**
 * Registers a maskable field into the current form's runtime registry so the
 * per-tab field drawer can list it. Safe to call multiple times for the same
 * field name (ref-counted); no-op when masking is disabled.
 */
export function useRegisterVisibilityField(
  fieldName: string,
  opts?: RegisterFieldOptions,
) {
  const { enabled, registerField } = useFormFieldVisibility();
  const tab = useCurrentTab();
  // Register multilingual variants (`x.fr`, `x.en`) once under their base key.
  const maskKey = splitFieldLocale(fieldName).baseKey;
  const labelKey = opts?.labelKey ?? maskKey;
  const label = opts?.label;
  const nonMaskable = opts?.nonMaskable;
  const skip = opts?.skip;

  useEffect(() => {
    if (!enabled || skip) return;
    return registerField({ name: maskKey, labelKey, label, tab, nonMaskable });
  }, [enabled, skip, registerField, maskKey, labelKey, label, tab, nonMaskable]);
}

/**
 * Keeps a field listed in the "Champs" drawer without rendering its control.
 * Use for inputs that only mount inside a dialog: otherwise masking them while
 * the dialog is open leaves no drawer entry to restore them once it closes.
 */
export function RegisterVisibilityField({
  name,
  labelKey,
  label,
}: {
  name: string;
  labelKey?: string;
  label?: string;
}) {
  useRegisterVisibilityField(name, { labelKey, label });
  return null;
}

/**
 * Forms where per-field masking must be entirely unavailable: the whole CMS
 * management area (every `cms-*` form) and the settings/"Réglages" page.
 * On these, the client requires all fields to always be visible — no eye
 * toggle, no "Champs"/"Onglets" menus, nothing hideable.
 */
export function isMaskingDisabledForForm(formKey: string): boolean {
  return formKey.startsWith("cms-") || formKey === "settings";
}

/**
 * Disables the per-field masking system for a whole subtree: every field stays
 * visible, no eye toggle renders and no field self-registers in the "Champs"
 * drawer. Used by zones where masking must never apply — e.g. the artwork
 * "Suivi financier" tab, whose dialogs (montant, date…) would otherwise lose
 * their inputs entirely after a "Tout masquer".
 *
 * Tab-level visibility and the persisted grid are left untouched: only how the
 * fields of this subtree read the system is overridden.
 */
export function FieldMaskingDisabledScope({
  children,
}: {
  children: ReactNode;
}) {
  const parent = useFormFieldVisibility();
  const value = useMemo<FormFieldVisibilityContextValue>(
    () => ({
      ...parent,
      enabled: false,
      allFieldsMinimized: false,
      isExpanded: () => true,
      canToggle: () => false,
      toggleVisibility: noop,
      minimizeAllFields: noop,
      expandAllFields: noop,
      registerField: () => noop,
      getRegisteredFields: () => [],
    }),
    [parent],
  );

  return (
    <FormFieldVisibilityContext.Provider value={value}>
      {children}
    </FormFieldVisibilityContext.Provider>
  );
}

type FormFieldVisibilityProviderProps = {
  formKey: string;
  children: ReactNode;
  /**
   * When true, every field is forced visible and non-toggleable regardless of
   * the persisted grid (used by the simplified/"Essentiel" view, which shows
   * its curated fields even if they are masked in the complete/"Pro" view).
   * The user's grid is left untouched — only how it is read is overridden.
   */
  forceAllVisible?: boolean;
};

export function FormFieldVisibilityProvider({
  formKey,
  children,
  forceAllVisible = false,
}: FormFieldVisibilityProviderProps) {
  const preferenceKey = getFormFieldsPreferenceKey(formKey);
  // Masking is unavailable for CMS/settings forms: force every field visible
  // (like the simplified view) AND report the system as disabled so that no
  // masking UI renders (eye toggles, "Champs"/"Onglets" menus, "Tout masquer").
  const maskingDisabled = isMaskingDisabledForForm(formKey);
  const allVisible = forceAllVisible || maskingDisabled;

  // Per-instance default: new instances start with every maskable field hidden
  // ("tout masquer") until each user shows them. Read non-throwingly so the
  // provider still works outside an AuthProvider (e.g. isolated previews).
  const auth = useContext(AuthContext);
  const instanceDefaultMasked =
    !maskingDisabled && Boolean(auth?.instanceData?.defaultFieldsMasked);

  // Resolve the initial grid. We must distinguish "no saved preference"
  // (`undefined` → apply the instance default) from an explicit empty grid
  // (`{}` → the user chose to show everything), so we never re-mask someone who
  // deliberately expanded all fields.
  const resolveVisibility = (): FormFieldsVisibilityState => {
    const stored = readBoPreference<FormFieldsVisibilityState>(preferenceKey);
    if (stored !== undefined) return stored;
    return instanceDefaultMasked
      ? { [FORM_FIELDS_ALL_MINIMIZED_KEY]: true }
      : {};
  };

  const [visibility, setVisibility] =
    useState<FormFieldsVisibilityState>(resolveVisibility);
  useEffect(() => {
    setVisibility(resolveVisibility());
    // `instanceDefaultMasked` can flip once instance data loads; re-resolve then,
    // which only affects the "no saved preference" case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceKey, instanceDefaultMasked]);

  const persist = useCallback(
    (next: FormFieldsVisibilityState) => {
      persistBoPreference(preferenceKey, next);
    },
    [preferenceKey],
  );

  const isExpanded = useCallback(
    (fieldName: string) =>
      allVisible ? true : isFieldExpanded(visibility, fieldName),
    [visibility, allVisible],
  );

  const canToggle = useCallback(
    (_fieldName: string, opts?: MaskableFieldOptions) => {
      if (allVisible) return false;
      if (opts?.nonMaskable) return false;
      if (opts?.maskable === false) return false;
      return true;
    },
    [allVisible],
  );

  const toggleVisibility = useCallback(
    (fieldName: string, opts?: MaskableFieldOptions) => {
      if (!canToggle(fieldName, opts)) return;
      setVisibility((prev) => {
        const currentlyExpanded = isFieldExpanded(prev, fieldName);
        const next = {
          ...prev,
          [fieldName]: !currentlyExpanded,
        };
        persist(next);
        return next;
      });
    },
    [canToggle, persist],
  );

  const minimizeAllFields = useCallback(() => {
    setVisibility((prev) => {
      // Reset per-field overrides but keep tab-level visibility.
      const next: FormFieldsVisibilityState = {
        [FORM_FIELDS_ALL_MINIMIZED_KEY]: true,
        ...(prev.__tabs ? { __tabs: prev.__tabs } : {}),
      };
      persist(next);
      return next;
    });
  }, [persist]);

  const expandAllFields = useCallback(() => {
    setVisibility((prev) => {
      // Reset field-level overrides, keep tab-level visibility.
      const next: FormFieldsVisibilityState = {
        ...(prev.__tabs ? { __tabs: prev.__tabs } : {}),
      };
      persist(next);
      return next;
    });
  }, [persist]);

  const isTabVisibleCb = useCallback(
    (tabId: string) =>
      allVisible ? true : isTabVisible(visibility, tabId),
    [visibility, allVisible],
  );

  const setTabVisible = useCallback(
    (tabId: string, visible: boolean) => {
      setVisibility((prev) => {
        const next = setTabVisibility(prev, tabId, visible);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const toggleTabVisibility = useCallback(
    (tabId: string) => {
      setVisibility((prev) => {
        const next = setTabVisibility(prev, tabId, !isTabVisible(prev, tabId));
        persist(next);
        return next;
      });
    },
    [persist],
  );

  // -- Runtime field registry --------------------------------------------------
  const registryRef = useRef<
    Map<string, { field: RegisteredVisibilityField; count: number }>
  >(new Map());
  const [registryVersion, setRegistryVersion] = useState(0);

  const registerField = useCallback((field: RegisteredVisibilityField) => {
    const map = registryRef.current;
    const existing = map.get(field.name);
    if (existing) {
      existing.count += 1;
      // Keep a previously provided label if this registration has none, so the
      // order of ref-counted registrations doesn't drop a resolved label.
      existing.field = {
        ...field,
        label: field.label ?? existing.field.label,
      };
    } else {
      map.set(field.name, { field, count: 1 });
    }
    setRegistryVersion((v) => v + 1);
    return () => {
      const cur = map.get(field.name);
      if (!cur) return;
      cur.count -= 1;
      if (cur.count <= 0) map.delete(field.name);
      setRegistryVersion((v) => v + 1);
    };
  }, []);

  const getRegisteredFields = useCallback(
    () => Array.from(registryRef.current.values()).map((v) => v.field),
    // registryVersion drives re-computation for consumers reading via memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registryVersion],
  );

  const allFieldsMinimized = allVisible
    ? false
    : isAllFieldsMinimized(visibility);
  const hiddenTabIds = useMemo(
    () => (allVisible ? [] : getHiddenTabIds(visibility)),
    [visibility, allVisible],
  );

  const value = useMemo(
    () => ({
      // Report the system as OFF for CMS/settings so no masking UI renders.
      enabled: !maskingDisabled,
      allFieldsMinimized,
      visibility,
      isExpanded,
      canToggle,
      toggleVisibility,
      minimizeAllFields,
      expandAllFields,
      isTabVisible: isTabVisibleCb,
      toggleTabVisibility,
      setTabVisible,
      hiddenTabIds,
      registerField,
      registryVersion,
      getRegisteredFields,
    }),
    [
      maskingDisabled,
      allFieldsMinimized,
      visibility,
      isExpanded,
      canToggle,
      toggleVisibility,
      minimizeAllFields,
      expandAllFields,
      isTabVisibleCb,
      toggleTabVisibility,
      setTabVisible,
      hiddenTabIds,
      registerField,
      registryVersion,
      getRegisteredFields,
    ],
  );

  return (
    <FormFieldVisibilityContext.Provider value={value}>
      {children}
    </FormFieldVisibilityContext.Provider>
  );
}
